import { Individual, individualsService, Order } from '@aslanpay/portal-common-lib';
import {
  Center,
  Flex,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { Table } from 'app/components/UI/Table/Table';
import { defaultPageSize } from 'app/constants/table';
import useFetchOrdersPage from 'app/hooks/useFetchOrdersPage/useFetchOrdersPage';
import individualUtils from 'app/utils/individuals/individualUtils';
import getOrdersColumns from 'app/utils/table/orders';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { OrdersActions } from '../UI/Table/Table.types';

import OrderArchiveModal from './OrderArchiveModal';
import OrderCompleteModal from './OrderCompleteModal';
import OrderDetailsModal from './OrderDetailsModal';

function OrdersTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });

  const {
    isOpen: isOrderDetailsModalOpen,
    onOpen: onOpenOrderDetailsModal,
    onClose: onCloseOrderDetailsModal,
  } = useDisclosure();

  const {
    isOpen: isOrderCompleteModalOpen,
    onOpen: onOpenOrderCompleteModal,
    onClose: onCloseOrderCompleteModal,
  } = useDisclosure();

  const {
    isOpen: isOrderArchiveModalOpen,
    onOpen: onOpenOrderArchiveModal,
    onClose: onCloseOrderArchiveModal,
  } = useDisclosure();

  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  // it helps to avoid fetching all tabs data on initial load and tab change
  // it also help to show the number of items on the awaiting approval tab
  const { data: requestedOrders, isLoading: isLoadingRequestedOrders } = useFetchOrdersPage({
    statuses: ['REQUESTED'],
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const { data: completedOrders, isLoading: isLoadingCompletedOrders } = useFetchOrdersPage({
    statuses: ['COMPLETED'],
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const { data: archivedOrders, isLoading: isLoadingArchivedOrders } = useFetchOrdersPage({
    statuses: ['ARCHIVED'],
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const NIL_UUID = '00000000-0000-0000-0000-000000000000';

  const viewOrder = async (order: Order) => {
    setSelectedOrder(order);
    setIsLoadingOrderDetails(true);
    try {
      if (order.createdBy && order.createdBy !== NIL_UUID) {
        const individual = await individualsService.fetchIndividual(order.createdBy);
        setSelectedIndividual(individual);
      } else {
        setSelectedIndividual(null);
      }
      onOpenOrderDetailsModal();
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  const completeOrder = (order: Order) => {
    setSelectedOrder(order);
    onOpenOrderCompleteModal();
  };

  const archiveOrder = (order: Order) => {
    setSelectedOrder(order);
    onOpenOrderArchiveModal();
  };

  const actions: OrdersActions = {
    view: viewOrder,
    complete: completeOrder,
    archive: archiveOrder,
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
      },
      { replace: true }
    );
  }, [pageIndex, sorting, setSearchParams]);

  return (
    <Flex flexDir="column" flexGrow={1} gap="20px">
      {selectedOrder && (
        <OrderDetailsModal
          isOpen={isOrderDetailsModalOpen}
          onClose={onCloseOrderDetailsModal}
          order={selectedOrder}
          createdByName={(() => {
            if (selectedIndividual) return individualUtils.individualFullName(selectedIndividual);
            if (selectedOrder.createdBy === NIL_UUID) return 'System';
            return 'N/A';
          })()}
        />
      )}
      {selectedOrder && (
        <OrderCompleteModal
          orderId={selectedOrder?.id || ''}
          isOpen={isOrderCompleteModalOpen}
          onClose={onCloseOrderCompleteModal}
        />
      )}
      {selectedOrder && (
        <OrderArchiveModal
          orderId={selectedOrder?.id || ''}
          isOpen={isOrderArchiveModalOpen}
          onClose={onCloseOrderArchiveModal}
        />
      )}
      <Tabs variant="unstyled" onChange={(index) => setActiveTabIndex(index)}>
        <TabList>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Awaiting payment ({requestedOrders?.total || 0})
          </Tab>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Paid
          </Tab>
          <Tab
            borderBottom="1px solid #E2E8F0"
            _selected={{
              color: 'brand.purple',
              borderBottom: '2px solid',
              borderColor: 'brand.purple',
            }}
            px={4}
            py={2}
            fontWeight="medium"
          >
            Archived
          </Tab>
          <Spacer />
        </TabList>

        <TabPanels mt={4}>
          <TabPanel p={0}>
            {isLoadingRequestedOrders ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getOrdersColumns(
                  'requested',
                  actions,
                  isLoadingOrderDetails,
                  selectedOrder?.id
                )}
                data={requestedOrders?.list || []}
                total={requestedOrders?.total || 0}
                pageNumber={requestedOrders?.pageNumber || pageIndex}
                pageSize={requestedOrders?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>

          <TabPanel p={0}>
            {activeTabIndex !== 1 && isLoadingCompletedOrders ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getOrdersColumns('completed', actions)}
                data={completedOrders?.list || []}
                total={completedOrders?.total || 0}
                pageNumber={completedOrders?.pageNumber || pageIndex}
                pageSize={completedOrders?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>

          <TabPanel p={0}>
            {activeTabIndex !== 2 && isLoadingArchivedOrders ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Table
                columns={getOrdersColumns('archived', actions)}
                data={archivedOrders?.list || []}
                total={archivedOrders?.total || 0}
                pageNumber={archivedOrders?.pageNumber || pageIndex}
                pageSize={archivedOrders?.pageSize || pageSize}
                onPaginationChange={setPagination}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}

export default OrdersTab;
